import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { PreInterviewQuestionsService } from "../pre-interview-questions.service";
import { Answer } from "../pre-interview-questions-typings";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ApiService } from "../../api.service";
import { EnvironmentToken } from "../../../util";
import { Environment, PreInterviewQuestion } from "../../../typings";
import { GatherApiService } from "../../gather/gather-services/gather-api.service";
import { SharedDataService } from "../../gather/gather-services/shared-data.service";
import { QuestionType } from "../../enums";

@Component({
    selector: 'app-answer-pre-interview-questions',
    templateUrl: './answer-pre-interview-questions.component.html',
    styleUrls: ['./answer-pre-interview-questions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AnswerPreInterviewQuestionsComponent implements OnInit {
  source: string;
  division: string;
  postingId: number;
  personId: number;
  isApplication: boolean;
  isRegistration: boolean;
  showJobPreferences: boolean;
  showEeocForm: boolean;
  newForm: boolean;
  talentCountryId: number;
  dataLoaded: Promise<boolean>;
  questionsForPosting: PreInterviewQuestion[];
  customQuestionsForPosting: PreInterviewQuestion[];
  formGroup: UntypedFormGroup;
  isGather = false;
  submitBtnDisabled: boolean = false;
  showSpinner: boolean = false;

  constructor(private router: Router,
              private route: ActivatedRoute,
              private apiService: ApiService,
              private questionService: PreInterviewQuestionsService,
              private gatherApiService: GatherApiService,
              private sharedDataService: SharedDataService,
              @Inject(EnvironmentToken) private environment: Environment) {
    this.isGather = this.route.snapshot.queryParamMap.get('source') == 'gather';
    if (!this.isGather) {
      this.apiService.getUserInfo(false).subscribe(
        person => {
          this.personId = person.id;
        },
        error => {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          console.error(`Error retrieving person by uuid: ${error.message}`);
        });
    }
  }

  ngOnInit(): void {
    this.postingId = parseInt(this.route.snapshot.queryParamMap.get('postingId'));
    this.talentCountryId = parseInt(this.route.snapshot.queryParamMap.get('talentCountryId'));
    this.showEeocForm = this.route.snapshot.queryParamMap.get('showEeocForm') === 'true';

    if (this.isGather) {
      this.source = 'gather';
      this.division = this.route.snapshot.queryParamMap.get('division');
      this.personId = parseInt(this.route.snapshot.queryParamMap.get('personId'));
    } else {
      this.talentCountryId = parseInt(this.route.snapshot.queryParamMap.get('talentCountryId'));
      this.isApplication = this.route.snapshot.queryParamMap.get('isApplication') === 'true';
      this.isRegistration = this.route.snapshot.queryParamMap.get('isRegistration') === 'true';
      this.showJobPreferences = this.route.snapshot.queryParamMap.get('showJobPreferences') === 'true';
      this.newForm = this.route.snapshot.queryParamMap.get('newForm') === 'true';
    }

    this.getSelectedQuestions();
  }

  getSelectedQuestions() : void {
    this.questionService.getQuestionsForPostingId(this.postingId).subscribe(
      questions => {
        this.buildQuestions(questions);
        this.buildFormGroup();
      },
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      error => console.log(`Failed to read questions for posting: ${this.postingId}. Error: ${error}`),
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      () => (this.dataLoaded = Promise.resolve(true))
    )
  }

  submitAnswers() : void {
    const preInterviewAnswers: Answer[] = [];
    let trackingId;
    this.questionsForPosting.forEach(question => {
      const name = `question${question.questionId}`;
      const answer = {} as Answer;
      answer.orderPreInterviewQuestionId = question.orderPreinterviewQuestionId;
      answer.answerValue = this.formGroup.controls[name].value;
      answer.question = question.description;
      answer.questionTypeId = question.questionTypeId
      preInterviewAnswers.push(answer);
    });
    this.customQuestionsForPosting.forEach(question => {
      const name = `question${question.customQuestionId}`;
      const answer = {} as Answer;
      answer.orderPreInterviewQuestionId = question.orderPreinterviewQuestionId;
      answer.answerValue = this.formGroup.controls[name].value;
      answer.question = question.description;
      preInterviewAnswers.push(answer);
    });

    if (this.source === 'gather') {
      trackingId = this.route.parent.snapshot.params.trackingId;
    }
    if (this.formGroup.valid) {
      this.submitBtnDisabled = true;
      this.showSpinner = true;
      this.questionService.savePreInterviewAnswers(this.postingId, this.personId, trackingId, preInterviewAnswers, this.talentCountryId).subscribe(
        ()=>{
          if (this.source == 'gather') {
            this.gatherApiService.saveAvailabilityMoats(this.route.parent.snapshot.params.trackingId, this.sharedDataService.talentAvailability.getValue(), true).subscribe(
              () => this.redirectAfterSave(),
              error => console.error("Failed to save gather availability. Error: " + error)
            );
          }
        }, error => console.error("Failed to save pre interview questions. Error: " + error),
        () => this.redirectAfterSave()
      );
    }
  }

  redirectAfterSave() {
    if (this.source === 'gather') {
      const trackingId = this.route.parent.snapshot.params.trackingId
      if (this.showEeocForm) {
        void this.router.navigate(['gather', trackingId, 'eeoc-form'], {
          queryParams: {
            source: 'gather',
            division: this.division
          }
        });
      } else {
        void this.router.navigate(['gather', trackingId, 'confirm'])
      }
    } else {
      let path = `myaquent/registration/`;
      if (this.showJobPreferences) {
        path += `job-preferences?talentCountryId=${this.talentCountryId}&isApplication=${String(this.isApplication)}&isRegistration=${String(
          this.isRegistration)}&personId=${this.personId}&newForm=${this.newForm}&showEeocForm=${this.showEeocForm}`;
      } else if (this.showEeocForm) {
        path += `eeoc-page?talentCountryId=${this.talentCountryId}&isApplication=${String(this.isApplication)}&isRegistration=${String(
          this.isRegistration
        )}`;
      } else {
        path += `talent/registration/success?isApplication=${String(this.isApplication)}&isRegistration=${String(this.isRegistration)}`;
      }
      top.location.replace(`${this.environment.matHost}/${path}`);
    }
  }

  buildFormGroup() {
    const group = {};
    this.questionsForPosting.forEach(question => {
      let formControl;
        formControl = new UntypedFormControl('');
        group[`question${question.questionId}`] = formControl;
    });
    this.customQuestionsForPosting.forEach(question => {
      let formControl;
      formControl = new UntypedFormControl('');
      if(question.questionTypeId === QuestionType.SHORT_TEXT || question.questionTypeId === QuestionType.LONG_TEXT) {
        group[`question${question.customQuestionId}`] = formControl;
      }
    });
    this.formGroup = new UntypedFormGroup(group);
  }

  buildQuestions(questions: PreInterviewQuestion[]): void {
    var questionList: any = [];
    var customQuestionList: any = [];
    questions.forEach(question => {
      if (question.questionTypeId === QuestionType.MULTIPLE_CHOICE) {
        question.optionChoiceForUI = question.optionChoiceList.map(option => {
          return {
            value: option.description,
            label: option.description
          };
        });
      }
      if (question.questionCategory === 'STATIC' && question.isActive) {
        questionList.push(question);
      } else if (question.isActive) {
        customQuestionList.push(question);
      }
    });
    this.questionsForPosting = questionList;
    this.customQuestionsForPosting = customQuestionList;
  }

  backToPosting(): void {
    const trackingId = this.route.parent.snapshot.params.trackingId
    this.router.navigate(['gather', trackingId, 'main']);
  }

  public get QuestionType() {
    return QuestionType;
  }
}
